/**
 * Shared 2FA Verification Form
 * Used by all login pages when backend returns requires_2fa: true
 */
import React, { useState, useRef, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ShieldCheck, Loader2, ArrowLeft, Mail } from 'lucide-react';

function maskEmail(email) {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const show = Math.min(2, local.length);
  return local.slice(0, show) + '***@' + domain;
}

const TwoFactorVerify = ({ email, userId, inviteCode, onSuccess, onBack }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const payload = { email, code: fullCode, user_id: userId };
      if (inviteCode) payload.invite_code = inviteCode;
      const { data } = await api.post('/auth/login/2fa', payload);

      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        if (data.role) localStorage.setItem('user_role', data.role);
        if (data.org_type) localStorage.setItem('org_type', data.org_type);
        if (data.user_id) localStorage.setItem('user_id', data.user_id);
        if (data.selected_membership_id) localStorage.setItem('active_membership_id', data.selected_membership_id);
        toast.success('Verification successful!');
        onSuccess(data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid code';
      toast.error(detail);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(30);
    try {
      await api.post('/auth/resend-2fa', { email });
      toast.info('A new verification code has been sent. Your previous code is no longer valid.');
    } catch (e) {
      toast.error('Failed to resend code. Please try again.');
    }
    setCode(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
  };

  return (
    <Card className="w-full max-w-sm mx-auto shadow-lg" data-testid="2fa-verify-form">
      <CardHeader className="text-center pb-2">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <ShieldCheck className="h-6 w-6 text-blue-600" />
        </div>
        <CardTitle className="text-lg">Verify Your Identity</CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Verification code sent to <span className="font-medium">{maskEmail(email)}</span>
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {code.map((digit, i) => (
              <Input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="w-11 h-12 text-center text-xl font-bold"
                data-testid={`2fa-digit-${i}`}
              />
            ))}
          </div>

          <Button
            type="submit"
            disabled={loading || code.join('').length !== 6}
            className="w-full"
            data-testid="2fa-verify-btn"
          >
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</> : 'Verify & Sign In'}
          </Button>

          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={onBack} className="text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Back to login
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className={`flex items-center gap-1 ${resendCooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}
            >
              <Mail className="h-3 w-3" />
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
            </button>
          </div>

          <p className="text-center text-[10px] text-gray-400">Code expires in 10 minutes</p>
        </form>
      </CardContent>
    </Card>
  );
};

export default TwoFactorVerify;
